import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Skeleton,
  Typography,
  notification
} from 'antd';
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { MobileOutlined } from '@ant-design/icons';
import TotpEnrolmentFlow from './TotpEnrolmentFlow';
import RecoveryCodesModal from './RecoveryCodesModal';
import RecoveryCodesReauth from './RecoveryCodesReauth';
import { logout as logoutAction } from '../../../redux/auth/actions';
import {
  clearPostLoginFlag,
  getMyMfa,
  messageFor,
  readPostLoginFlags,
  resetOwnPassword,
  type MfaStatus
} from '../../../service/mfa.service';
import { SESSION_EXPIRED } from './mfaErrors';

/**
 * The three flags every successful sign-in carries, turned into screens.
 *
 * | flag | when true | what happens here |
 * | :--- | :--- | :--- |
 * | `mfa_enrolment_required` | the account has neither factor | blocking enrolment popup |
 * | `recovery_codes_required` | it has a factor and no unused codes | blocking codes popup |
 * | `reenrol_totp_required` | this sign-in used a fallback, so TOTP was removed | a prompt |
 *
 * The first two are an **ordered pair, never a combination**. You get the first until a
 * factor exists, then the second until codes do, so they are an `if/else if` rather than two
 * independent conditions.
 *
 * `recovery_codes_required` is the one that matters on day one: every account that enrolled a
 * factor before recovery codes shipped has none, and nothing else would ever tell them. It
 * also returns to true when someone spends all ten, because the condition is "no unused
 * codes", not "never generated", which is exactly when being told matters.
 *
 * Mounted in `AppContent` rather than `DefaultLayout` so it survives navigation to
 * `/workspaces` and `/workspace/create`, which sit outside that layout.
 */

/** Screens that either have no session yet or are the middle of getting one. */
const EXEMPT_PREFIXES = [
  '/login', '/2fa', '/auth/', '/accept-invitation', '/invitations/', '/licence', '/_internal/knowledge-v2'
];

const isSignedIn = (): boolean => {
  try {
    const stored = sessionStorage.getItem('auth');
    return !!(stored && JSON.parse(stored)?.current?.access_token);
  } catch {
    return false;
  }
};

type Factor = 'totp' | null;

const SecurityGate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [needsEnrolment, setNeedsEnrolment] = useState(false);
  const [needsCodes, setNeedsCodes] = useState(false);

  const [passwordForm] = Form.useForm();
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [factor, setFactor] = useState<Factor>(null);
  const [enrolError, setEnrolError] = useState<string | null>(null);

  const [mfa, setMfa] = useState<MfaStatus | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const [issuedCodes, setIssuedCodes] = useState<string[]>([]);

  /** The server's `enrolment_enforced`, asked once. `null` until it has answered. */
  const [enrolmentEnforced, setEnrolmentEnforced] = useState<boolean | null>(null);

  const isExempt = EXEMPT_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  /**
   * Re-read on every navigation, not only on mount. Signing in through the challenge screen
   * reaches the app by `navigate()` in one of its branches: no reload, so this component is
   * already mounted when the flags first land in storage.
   */
  useEffect(() => {
    if (isExempt || !isSignedIn()) {
      setNeedsPasswordChange(false);
      setNeedsEnrolment(false);
      setNeedsCodes(false);
      return;
    }
    const flags = readPostLoginFlags();

    // Ordered sequence: password change -> MFA enrolment -> recovery codes
    setNeedsPasswordChange(flags.must_change_password);
    setNeedsEnrolment(
      !flags.must_change_password && flags.mfa_enrolment_required
    );
    setNeedsCodes(
      !flags.must_change_password &&
      !flags.mfa_enrolment_required &&
      flags.recovery_codes_required
    );

    // Non-blocking, and deliberately not raised behind a blocking popup: when a fallback
    // sign-in leaves an account with no factor at all, the enrolment popup is already saying
    // this louder.
    if (
      flags.reenrol_totp_required &&
      !flags.mfa_enrolment_required &&
      !flags.recovery_codes_required
    ) {
      clearPostLoginFlag('reenrol_totp_required');
      notification.warning({
        message: 'Set up your authenticator again',
        description:
          'You signed in without your authenticator app, so it has been removed from your ' +
          'account. Set up a new one to get the extra step back.',
        duration: 0,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy();
              navigate('/profile?tab=security');
            }}
          >
            Set it up
          </Button>
        )
      });
    }
  }, [location.pathname, isExempt, navigate]);

  /** The codes popup has to know whether regenerating will be asked for an `otp`. */
  const loadMfa = useCallback(async () => {
    try {
      const { data } = await getMyMfa();
      setMfa(data);
      setMfaError(null);
    } catch (err: any) {
      setMfaError(
        messageFor(
          err,
          {
            401: SESSION_EXPIRED
          },
          'Cannot read your two-factor settings.'
        )
      );
    }
  }, []);

  /**
   * `mfa_enrolment_required` is whatever the server said at sign-in, and it stays in storage
   * for the whole session. If enrolment enforcement has been switched off since, that flag is
   * stale, so ask before raising the popup and drop the flag when the answer is no.
   *
   * Any failure to ask keeps the popup: that is what the stored flag says, and it is how this
   * behaved before the server could be asked.
   */
  useEffect(() => {
    if (!needsEnrolment || enrolmentEnforced !== null) return;
    getMyMfa()
      .then(({ data }) => {
        const enforced = data.enrolment_enforced !== false;
        setEnrolmentEnforced(enforced);
        if (!enforced) {
          clearPostLoginFlag('mfa_enrolment_required');
          setNeedsEnrolment(false);
        }
      })
      .catch(() => setEnrolmentEnforced(true));
  }, [needsEnrolment, enrolmentEnforced]);

  const hasLoadedMfa = useRef(false);
  useEffect(() => {
    if (!needsCodes || hasLoadedMfa.current) return;
    hasLoadedMfa.current = true;
    loadMfa();
  }, [needsCodes, loadMfa]);

  const signOut = () => {
    dispatch<any>(logoutAction());
    navigate('/login');
  };

  const signOutFooter = (
    <div className="text-center mt-6">
      <Button type="link" size="small" onClick={signOut}>
        Sign out instead
      </Button>
    </div>
  );

  const finishPasswordChange = async (values: any) => {
    setChangingPassword(true);
    setPasswordError(null);
    try {
      const stored = sessionStorage.getItem('auth');
      const email = stored ? JSON.parse(stored)?.current?.email : '';
      await resetOwnPassword(email, values.password, values.confirmPassword);

      clearPostLoginFlag('must_change_password');
      setNeedsPasswordChange(false);

      // Cascade to next flags in sequence
      const flags = readPostLoginFlags();
      setNeedsEnrolment(flags.mfa_enrolment_required);
      setNeedsCodes(!flags.mfa_enrolment_required && flags.recovery_codes_required);
    } catch (err: any) {
      setPasswordError(messageFor(err, {}, 'Cannot change password.'));
    } finally {
      setChangingPassword(false);
    }
  };

  const finishEnrolment = (codes?: string[]) => {
    clearPostLoginFlag('mfa_enrolment_required');
    setNeedsEnrolment(false);
    setFactor(null);
    if (codes?.length) {
      // Enrolling the first factor is what mints the first set, so the codes popup that would
      // otherwise follow is already answered.
      clearPostLoginFlag('recovery_codes_required');
      setIssuedCodes(codes);
    }
  };

  const finishCodes = (codes: string[]) => {
    clearPostLoginFlag('recovery_codes_required');
    setNeedsCodes(false);
    setIssuedCodes(codes);
  };

  if (isExempt) return null;

  return (
    <>
      {/* 0: forced password change on first login */}
      <Modal
        open={needsPasswordChange}
        title="Change your password"
        closable={false}
        maskClosable={false}
        keyboard={false}
        width={420}
        footer={null}
      >
        <Typography.Paragraph className="text-[13px] mb-6">
          You are using a default password. Choose a new one before you continue.
        </Typography.Paragraph>

        {passwordError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={passwordError}
            closable
            onClose={() => setPasswordError(null)}
          />
        )}

        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={finishPasswordChange}
          requiredMark={false}
        >
          <Form.Item
            name="password"
            label="New password"
            rules={[
              {
                required: true,
                message: 'Please enter a new password'
              }, {
                min: 8,
                message: 'Password must be at least 8 characters'
              }
            ]}
          >
            <Input.Password size="large" autoFocus />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: 'Please confirm your password'
              }, ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match'));
                }
              })
            ]}
          >
            <Input.Password size="large" />
          </Form.Item>

          <Button
            type="primary"
            block
            size="large"
            htmlType="submit"
            loading={changingPassword}
            className="mt-2"
          >
            Change password
          </Button>
        </Form>

        {signOutFooter}
      </Modal>

      {/* 1: no second factor at all */}
      <Modal
        open={needsEnrolment && enrolmentEnforced === true}
        title="Set up two-factor authentication"
        closable={false}
        maskClosable={false}
        keyboard={false}
        width={520}
        footer={null}
      >
        <Typography.Paragraph className="text-[13px]">
          Your account is protected by a password alone. Add a second step
          before you carry on. Either one is enough.
        </Typography.Paragraph>

        {enrolError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={enrolError}
            closable
            onClose={() => setEnrolError(null)}
          />
        )}

        {factor === null && (
          <>
            {/* The icon is laid out here rather than passed as antd's `icon` prop. That prop
                puts the icon in the button's inline flow, so a two-line label wraps its
                second line back to x=0: underneath the icon instead of aligned with the
                title. One flex row with `items-start` keeps the icon in its own column. */}
            <Button
              block
              size="large"
              className="mb-3 !h-auto !py-3 !text-left"
              onClick={() => {
                setEnrolError(null);
                setFactor('totp');
              }}
            >
              <span className="flex items-start gap-3 w-full">
                <MobileOutlined className="mt-[3px] shrink-0" />
                <span className="whitespace-normal">
                  <span
                    className="block font-semibold"
                  >Authenticator app</span>
                  <span className="block text-[12px] opacity-70 font-normal">
                    Recommended. A code from an app on your phone, which works
                    with no signal and no inbox.
                  </span>
                </span>
              </span>
            </Button>
          </>
        )}

        {factor === 'totp' && (
          <TotpEnrolmentFlow
            onEnrolled={finishEnrolment}
            onCancel={(reason) => {
              setFactor(null);
              setEnrolError(reason ?? null);
            }}
          />
        )}

        {signOutFooter}
      </Modal>

      {/* 2: a factor, but no unused recovery codes */}
      <Modal
        open={needsCodes}
        title="Generate your recovery codes"
        closable={false}
        maskClosable={false}
        keyboard={false}
        width={520}
        footer={null}
      >
        <Typography.Paragraph className="text-[13px]">
          You have a second factor but no recovery codes. These are ten
          single-use codes for one situation: the day you lose your second
          factor.
        </Typography.Paragraph>

        {/* The form is withheld until `GET /users/me/mfa` has answered, rather than rendered
            with a guessed `hasTotp`. Guessing `false` on an account that has an authenticator
            produces a 400 asking for a code that the form has no field for: a dead end the
            user cannot argue with. */}
        {mfaError ? (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={mfaError}
            action={
              <Button size="small" onClick={loadMfa}>
                Try again
              </Button>
            }
          />
        ) : !mfa ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          <RecoveryCodesReauth hasTotp={mfa.totp} onGenerated={finishCodes} />
        )}

        {signOutFooter}
      </Modal>

      <RecoveryCodesModal
        open={issuedCodes.length > 0}
        codes={issuedCodes}
        onClose={() => setIssuedCodes([])}
      />
    </>
  );
};

export default SecurityGate;
